import React, { useEffect, useState, useCallback, useRef } from 'react'
import * as THREE from 'three'
import { TweenMax, TimelineLite, Power1 } from 'gsap'
import './styles.scss'
import cantons from './cantons.yml'
import * as d3Scale from 'd3-scale'
import * as d3Color from 'd3-color'

interface Props {
  sceneId: string
  data: []
  isOnCalendar: boolean
}

export const Canvas = ({ sceneId, data, isOnCalendar }: Props) => {
  const activeMaterial = {
    color: {
      r: 0.81,
      g: 0.77,
      b: 0.23
    },
    emissive: {
      r: 0.5,
      g: 0.19,
      b: 0.06
    }
  }
  const emptyMaterial = {
    color: {
      r: 0.68,
      g: 0.64,
      b: 0.64
    },
    emissive: {
      r: 0.38,
      g: 0.39,
      b: 0.4
    }
  }
  const isMobile = window.innerWidth < 600
  const centerCameraPosition = { x: 3, y: isMobile ? 60 : 58, z: 16 }
  const centerTargetPosition = { x: 0, y: 0, z: 1 }

  const colorScale = d3Scale
    .scaleLinear()
    .domain([0, 100])
    .range(['#cfc43b', '#8f575c'])
  // .range(['#b89c0d', '#7d3352'])
  const emissiveScale = d3Scale
    .scaleLinear()
    .domain([0, 100])
    .range(['#80300f', '#660a47'])
  // .range(['#9c2900', '#5e001c'])
  const renderer = useRef(null)
  const camera = useRef(null)
  const chMesh = useRef(null)
  const indicator = useRef(null)
  const scene = useRef(null)
  const introAnimation = useRef(null)
  const cameraTargetTween = useRef(new TimelineLite())
  const cameraTargetMesh = useRef(null)
  const [showLegend, setShowLegend] = useState(false)
  const [debugColor, setDebugColor] = useState(emptyMaterial)
  const [debugLight, setDebugLight] = useState({ x: -21, y: -21, z: 35, i: 1 })
  const [debugCamera, setDebugCamera] = useState({ ...centerCameraPosition })
  // const indicatorMaterial = useRef(null)
  const indicatorSize = useRef(1)
  const activeCantons = []

  let frame = 0
  let lastIndicatorOpacity = 0

  const getCenterPoint = mesh => {
    const geometry = mesh.geometry
    geometry.computeBoundingBox()
    const center = geometry.boundingBox.getCenter(new THREE.Vector3())
    mesh.localToWorld(center)
    return center
  }

  const setActiveColor = objectName => {
    TweenMax.to(scene.current.getObjectByName(objectName).material.color, 2, {
      r: activeMaterial.color.r,
      g: activeMaterial.color.g,
      b: activeMaterial.color.b,
      ease: Power1.easeInOut
    })
    TweenMax.to(
      scene.current.getObjectByName(objectName).material.emissive,
      2,
      {
        r: activeMaterial.emissive.r,
        g: activeMaterial.emissive.g,
        b: activeMaterial.emissive.b,
        ease: Power1.easeInOut
      }
    )
  }

  const setCantonColor = (objectName, count) => {
    const c = count > 100 ? 100 : count
    const targetColor = d3Color.color(colorScale(c))
    const targetEmissive = d3Color.color(emissiveScale(c))

    // console.log(scene.current.getObjectByName(objectName).material)

    TweenMax.to(scene.current.getObjectByName(objectName).material.color, 2, {
      r: targetColor.r / 255,
      g: targetColor.g / 255,
      b: targetColor.b / 255,
      ease: Power1.easeInOut
    })
    TweenMax.to(
      scene.current.getObjectByName(objectName).material.emissive,
      2,
      {
        r: targetEmissive.r / 255,
        g: targetEmissive.g / 255,
        b: targetEmissive.b / 255,
        ease: Power1.easeInOut
      }
    )
  }

  const highlightAll = highlight => {
    if (highlight) {
      cantons.forEach(c => {
        setActiveColor(c)
      })
    } else {
      cantons.forEach(c => {
        const currentCanton = scene.current.getObjectByName(c)
        currentCanton.material.color = {
          r: emptyMaterial.color.r,
          g: emptyMaterial.color.g,
          b: emptyMaterial.color.b
        }
        currentCanton.material.emissive = {
          r: emptyMaterial.emissive.r,
          g: emptyMaterial.emissive.g,
          b: emptyMaterial.emissive.b
        }
      })
    }
  }

  const moveIndicatorTo = objectName => {
    let center = { ...centerTargetPosition }

    if (objectName !== 'CENTER') {
      const targetObject = scene.current.getObjectByName(objectName)
      const geometry = targetObject.geometry

      geometry.computeBoundingBox()
      center = geometry.boundingBox.getCenter(new THREE.Vector3())
      // What's this local position to the world?
      targetObject.localToWorld(center)
      // And what is this world position to the CH group?
      chMesh.current.worldToLocal(center)
    }

    // Position the indicator (animation)
    indicator.current.position.x = center.x
    indicator.current.position.y = center.y
    indicator.current.position.z = center.z
    indicator.current.visible = true
  }

  const moveTargetTo = (objectName, cameraPosition, duration) => {
    // Position the helper
    // get the geometry center
    let center = { ...centerTargetPosition }

    if (objectName !== 'CENTER') {
      const targetObject = scene.current.getObjectByName(objectName)
      const geometry = targetObject.geometry

      geometry.computeBoundingBox()
      center = geometry.boundingBox.getCenter(new THREE.Vector3())
      // What's this local position to the world?
      targetObject.localToWorld(center)
      // And what is this world position to the CH group?
      chMesh.current.worldToLocal(center)
    }

    // Animate the camera target position
    cameraTargetTween.current.clear()
    cameraTargetTween.current.to(cameraTargetMesh.current.position, duration, {
      x: center.x,
      y: center.y,
      z: center.z,
      ease: Power1.easeInOut
    })

    // Animate the camera position
    cameraTargetTween.current.to(
      camera.current.position,
      duration,
      {
        x: cameraPosition.x,
        y: cameraPosition.y,
        z: cameraPosition.z,
        ease: Power1.easeInOut
      },
      0
    )
  }

  useEffect(() => {
    const ignore = ['CH', 'date']

    if (data) {
      // console.log(sceneId)
      if (sceneId === 0) {
        clearInterval(introAnimation.current)
        highlightAll(false)
      }
      for (const d in data) {
        if (!ignore.includes(d)) {
          if (data[d] !== '') {
            // console.log(d + ': ' + data[d])
            setCantonColor(d, data[d])
          }
        }
      }
    }
  }, [data])

  useEffect(() => {
    if (scene.current) {
      cantons.forEach(c => {
        const currentCanton = scene.current.getObjectByName(c)
        currentCanton.material.color = {
          r: debugColor.color.r,
          g: debugColor.color.g,
          b: debugColor.color.b
        }
        currentCanton.material.emissive = {
          r: debugColor.emissive.r,
          g: debugColor.emissive.g,
          b: debugColor.emissive.b
        }
      })
    }
  }, [debugColor])

  useEffect(() => {
    if (scene.current) {
      const l = scene.current.getObjectByName('HemisphereLight')
      l.position.x = debugLight.x
      l.position.y = debugLight.y
      l.position.z = debugLight.z
      l.intensity = debugLight.i
    }

    // console.log(debugLight)
  }, [debugLight])

  useEffect(() => {
    if (scene.current) {
      const c = scene.current.getObjectByName('camera')
      c.position.x = debugCamera.x
      c.position.y = debugCamera.y
      c.position.z = debugCamera.z
    }

    // console.log(debugLight)
  }, [debugCamera])

  useEffect(() => {
    if (camera.current) {
      // console.log()

      switch (sceneId) {
        case 'intro':
          console.log('Scene INTRO')
          moveTargetTo('CENTER', centerCameraPosition, 2)
          indicatorSize.current = 1
          indicator.current.visible = false
          setShowLegend(false)
          break
        case '0-0':
          setShowLegend(true)
          moveTargetTo('TI', { x: 1, y: 40, z: 26 }, 2)
          moveIndicatorTo('TI')
          break
        case '0-1':
          moveTargetTo('NE', { x: -22, y: 42, z: 13 }, 3)
          moveIndicatorTo('NE')
          break
        case '1-0':
          moveTargetTo('GE', { x: -35, y: 26, z: 34 }, 2)
          moveIndicatorTo('GE')
          break
        case '2-0':
          moveTargetTo('GR', { x: 20, y: 62, z: 0 }, 2)
          moveIndicatorTo('GR')
          break
        case '2-1':
          moveTargetTo('ZH', { x: 0, y: 60, z: 0 }, 2)
          moveIndicatorTo('ZH')
          break
        case '2-2':
          moveTargetTo('AG', { x: 0, y: 60, z: 0 }, 2)
          moveIndicatorTo('AG')
          break
        case '2-3':
          moveTargetTo('BS', { x: -15, y: 34, z: -7 }, 2)
          moveIndicatorTo('BS')
          break
        case '2-4':
          moveTargetTo('VD', { x: -25, y: 41, z: 22 }, 2)
          moveIndicatorTo('VD')
          break
        case '3-0':
          moveTargetTo('BL', { x: 0, y: 60, z: 0 }, 2)
          moveIndicatorTo('BL')
          break
        case '3-1':
          moveTargetTo('VS', { x: -10, y: 49, z: 35 }, 2)
          moveIndicatorTo('VS')
          break
        case '3-2':
          moveTargetTo('CENTER', centerCameraPosition, 2)
          moveIndicatorTo('CENTER')
          indicatorSize.current = 7
          break
        case '3-3':
          moveTargetTo('CENTER', centerCameraPosition, 2)
          moveIndicatorTo('GE')
          indicatorSize.current = 1
          break
        case '3-4':
          moveTargetTo('CENTER', centerCameraPosition, 2)
          moveIndicatorTo('BS')
          break
        case '4-0':
          moveTargetTo('BE', { x: 0, y: 60, z: 0 }, 2)
          moveIndicatorTo('BE')
          break
        case '4-1':
          moveTargetTo('CENTER', centerCameraPosition, 2)
          moveIndicatorTo('CENTER')
          indicatorSize.current = 7
          break
        case '4-2':
          moveTargetTo('GR', { x: 0, y: 60, z: 0 }, 2)
          moveIndicatorTo('GR')
          indicatorSize.current = 1
          break
        case '5-0':
          moveTargetTo('FR', { x: 0, y: 60, z: 0 }, 2)
          moveIndicatorTo('FR')
          break
        case '5-1':
          moveTargetTo('AG', { x: 0, y: 60, z: 0 }, 2)
          moveIndicatorTo('AG')
          break
        case '6-0':
          moveTargetTo('NE', { x: 0, y: 60, z: 0 }, 2)
          moveIndicatorTo('NE')
          break
        case '7-0':
          moveTargetTo('TI', centerCameraPosition, 2)
          moveIndicatorTo('TI')
          break
        case '7-1':
          moveTargetTo('GE', { x: -41, y: 33, z: 37 }, 2)
          moveIndicatorTo('GE')
          break
        case '7-2':
          moveTargetTo('ZG', { x: 0, y: 60, z: 0 }, 2)
          moveIndicatorTo('ZG')
          break
        case '7-3':
          moveTargetTo('CENTER', centerCameraPosition, 2)
          moveIndicatorTo('CENTER')
          indicatorSize.current = 7
          break
        case '8-0':
          moveTargetTo('SZ', { x: 0, y: 60, z: 0 }, 2)
          moveIndicatorTo('SZ')
          indicatorSize.current = 1
          break
        case '8-1':
          moveTargetTo('CENTER', centerCameraPosition, 2)
          moveIndicatorTo('CENTER')
          indicatorSize.current = 7
          break
        case '8-2':
          moveTargetTo('VD', { x: 0, y: 60, z: 0 }, 2)
          moveIndicatorTo('VD')
          indicatorSize.current = 1
          break
        case '8-3':
          moveTargetTo('SG', { x: 0, y: 60, z: 0 }, 2)
          moveIndicatorTo('SG')
          break
        case '9-0':
          moveTargetTo('VD', { x: -26, y: 37, z: -10 }, 2)
          moveIndicatorTo('VD')
          break
        case '9-1':
          moveTargetTo('JU', { x: 0, y: 60, z: 0 }, 2)
          moveIndicatorTo('JU')
          break
        case '9-2':
          moveTargetTo('AI', { x: 0, y: 60, z: 0 }, 2)
          moveIndicatorTo('AI')
          break
        case '10-0':
          moveTargetTo('CENTER', centerCameraPosition, 2)
          moveIndicatorTo('CENTER')
          indicatorSize.current = 7
          break
        case '10-1':
          moveTargetTo('TG', { x: 0, y: 60, z: 0 }, 2)
          moveIndicatorTo('TG')
          indicatorSize.current = 1
          break
        case '10-2':
          moveTargetTo('SO', { x: 0, y: 60, z: 0 }, 2)
          moveIndicatorTo('SO')
          break
        case '11-0':
          moveTargetTo('CENTER', centerCameraPosition, 2)
          moveIndicatorTo('CENTER')
          indicatorSize.current = 7
          break
        case '11-1':
          moveTargetTo('TI', centerCameraPosition, 2)
          moveIndicatorTo('TI')
          indicatorSize.current = 1
          break
        case '12-0':
          moveTargetTo('BL', centerCameraPosition, 2)
          moveIndicatorTo('BL')
          indicatorSize.current = 1
          break
        case '12-1':
          moveTargetTo('CENTER', centerCameraPosition, 2)
          moveIndicatorTo('CENTER')
          indicatorSize.current = 7
          break
        case '13-0':
          moveTargetTo('CENTER', centerCameraPosition, 2)
          moveIndicatorTo('CENTER')
          break
        case '14-0':
          moveTargetTo('CENTER', centerCameraPosition, 2)
          moveIndicatorTo('VD')
          indicatorSize.current = 1
          break
        case '14-1':
          moveTargetTo('CENTER', centerCameraPosition, 2)
          moveIndicatorTo('TI')
          break
        case '14-2':
          moveTargetTo('CENTER', centerCameraPosition, 2)
          moveIndicatorTo('GE')
          break
        case '15-0':
          moveTargetTo('GE', centerCameraPosition, 2)
          moveIndicatorTo('GE')
          break
        case '15-1':
          moveTargetTo('CENTER', centerCameraPosition, 2)
          moveIndicatorTo('CENTER')
          indicatorSize.current = 7
          break
        case '15-2':
          moveTargetTo('TI', centerCameraPosition, 2)
          moveIndicatorTo('TI')
          indicatorSize.current = 1
          break
        case '16-0':
          moveTargetTo('CENTER', centerCameraPosition, 2)
          moveIndicatorTo('CENTER')
          indicatorSize.current = 7
          break
        case '16-1':
          moveTargetTo('CENTER', centerCameraPosition, 2)
          moveIndicatorTo('VD')
          indicatorSize.current = 1
          break
        case '16-2':
          moveTargetTo('CENTER', centerCameraPosition, 2)
          moveIndicatorTo('NE')
          break
        case '16-3':
          moveTargetTo('CENTER', centerCameraPosition, 2)
          moveIndicatorTo('CENTER')
          indicatorSize.current = 7
          break
        case '17-1':
          moveTargetTo('NE', { x: 0, y: 60, z: 0 }, 2)
          moveIndicatorTo('NE')
          indicatorSize.current = 1
          break
        case '17-2':
          moveTargetTo('FR', { x: 0, y: 60, z: 0 }, 2)
          moveIndicatorTo('FR')
          break
        case '17-3':
          moveTargetTo('OW', { x: 0, y: 60, z: 0 }, 2)
          moveIndicatorTo('OW')
          break
        case '17-4':
          moveTargetTo('VS', { x: 0, y: 60, z: 0 }, 2)
          moveIndicatorTo('VS')
          break
        case '17-5':
          moveTargetTo('VD', centerCameraPosition, 2)
          moveIndicatorTo('VD')
          break
        case '17-6':
          moveTargetTo('GE', centerCameraPosition, 2)
          moveIndicatorTo('GE')
          break
        case '17-7':
          moveTargetTo('CENTER', centerCameraPosition, 2)
          moveIndicatorTo('CENTER')
          indicatorSize.current = 7
          break
        case '18-0':
          moveTargetTo('CENTER', centerCameraPosition, 2)
          moveIndicatorTo('CENTER')
          indicatorSize.current = 7
          break
        case '18-1':
          moveTargetTo('TI', centerCameraPosition, 2)
          moveIndicatorTo('TI')
          indicatorSize.current = 1
          break
        case '18-2':
          moveTargetTo('CENTER', centerCameraPosition, 2)
          moveIndicatorTo('VD')
          break
        case '19-0':
          moveTargetTo('CENTER', centerCameraPosition, 2)
          moveIndicatorTo('CENTER')
          indicatorSize.current = 7
          break
        case '20-0':
          moveTargetTo('CENTER', centerCameraPosition, 2)
          moveIndicatorTo('CENTER')
          indicatorSize.current = 7
          break
        case '21-0':
          moveTargetTo('CENTER', centerCameraPosition, 2)
          moveIndicatorTo('CENTER')
          indicatorSize.current = 1
          break
        case '21-1':
          moveTargetTo('TI', centerCameraPosition, 2)
          moveIndicatorTo('TI')
          break
        case '21-2':
          moveTargetTo('NE', centerCameraPosition, 2)
          moveIndicatorTo('NE')
          break
        case '22-0':
          moveTargetTo('CENTER', centerCameraPosition, 2)
          moveIndicatorTo('CENTER')
          indicatorSize.current = 7
          break
        case '22-2':
          moveTargetTo('TI', centerCameraPosition, 2)
          moveIndicatorTo('TI')
          indicatorSize.current = 1
          break
        case '22-3':
          moveTargetTo('BE', centerCameraPosition, 2)
          moveIndicatorTo('BE')
          indicatorSize.current = 0
          break
        case '23-0':
          moveTargetTo('FR', { x: 0, y: 50, z: 0 }, 2)
          moveIndicatorTo('FR')
          indicatorSize.current = 1
          break
        case '23-1':
          moveTargetTo('GE', { x: 0, y: 50, z: 0 }, 2)
          moveIndicatorTo('GE')
          break
        case '23-2':
          moveTargetTo('CENTER', centerCameraPosition, 2)
          moveIndicatorTo('CENTER')
          indicatorSize.current = 7
          break
        case '24-0':
          moveTargetTo('CENTER', centerCameraPosition, 2)
          moveIndicatorTo('CENTER')
          indicatorSize.current = 0
          break
      }
    }
  }, [sceneId])

  const animate = () => {
    requestAnimationFrame(animate)

    // Slightly animate the map
    chMesh.current.rotation.x = (0 * Math.PI) / 180 + Math.cos(frame / 3) / 5
    chMesh.current.rotation.y = Math.sin(frame / 3) / 10
    chMesh.current.rotation.z = Math.sin(frame / 3) / 10

    // Animate the map in interaction with the user
    // TODO

    // Animate the indicator
    const indicatorOpacity =
      1 -
      (Math.abs(Math.sin(frame * 5)) < 0 ? 0 : Math.abs(Math.sin(frame * 5)))
    indicator.current.material.opacity =
      indicatorOpacity >= lastIndicatorOpacity ? 0 : indicatorOpacity * 0.45
    indicator.current.scale.x = Math.sin(frame * 5) * indicatorSize.current
    indicator.current.scale.y = Math.sin(frame * 5) * indicatorSize.current
    indicator.current.scale.z = Math.sin(frame * 5) * indicatorSize.current
    lastIndicatorOpacity = indicatorOpacity

    // The camera always looks to its target
    camera.current.lookAt(getCenterPoint(cameraTargetMesh.current))

    frame += 0.01
    renderer.current.render(scene.current, camera.current)
  }

  const init = () => {
    const loader = new THREE.ObjectLoader()
    loader.parse(require('./scene.json'), s => {
      // Set the new scene
      scene.current = s
      console.log(s)

      // Register the camera
      camera.current = scene.current.getObjectByName('camera')
      camera.current.position.x = centerCameraPosition.x
      camera.current.position.y = centerCameraPosition.y
      camera.current.position.z = centerCameraPosition.z
      camera.current.up.set(0, 0, -1)
      // camera.current.near = 20
      // camera.current.far = 1000

      // Register the switzerland group
      chMesh.current = scene.current.getObjectByName('switzerland')

      // Initialize the light values
      const light = scene.current.getObjectByName('HemisphereLight')
      light.position.x = 5
      light.position.y = 215
      light.position.z = -300
      light.intensity = 1

      setDebugLight({
        x: light.position.x,
        y: light.position.y,
        z: light.position.z,
        i: light.intensity
      })

      // Register the indicator (animation)
      indicator.current = scene.current.getObjectByName('Sphere')
      indicator.current.material.color = { r: 1, g: 1, b: 1 }
      indicator.current.material.emissive = { r: 1, g: 1, b: 1 }
      indicator.current.visible = false

      // Create an invisible camera target geometry
      cameraTargetMesh.current = new THREE.Mesh(
        new THREE.BoxGeometry(0.5, 1, 0.5),
        new THREE.MeshNormalMaterial()
      )
      cameraTargetMesh.current.visible = false
      cameraTargetMesh.current.position.x = centerTargetPosition.x
      cameraTargetMesh.current.position.y = centerTargetPosition.y
      cameraTargetMesh.current.position.z = centerTargetPosition.z
      chMesh.current.add(cameraTargetMesh.current)

      // Make a unique material for each canton
      cantons.forEach(c => {
        const thisCanton = scene.current.getObjectByName(c)
        thisCanton.material = thisCanton.material.clone()
        thisCanton.material.color = { ...emptyMaterial.color }
        thisCanton.material.emissive = { ...emptyMaterial.emissive }
      })

      // highlightAll(false)
      camera.current.aspect = window.innerWidth / window.innerHeight
      camera.current.updateProjectionMatrix()

      // Start the intro animation
      introAnimation.current = setInterval(() => {
        // Get a random canton
        const cantonName = cantons[Math.floor(Math.random() * cantons.length)]
        activeCantons.push(cantonName)
        setActiveColor(cantonName)
        // moveTargetTo(cantonName, { x: 0, y: 0, z: 50 }, 10)
      }, 2000)
      animate()
    })
  }

  const resize = () => {
    /* setDimensions({
      width: window.innerWidth,
      height: window.innerHeight
    }) */
    if (camera.current) {
      camera.current.aspect = window.innerWidth / window.innerHeight
      camera.current.updateProjectionMatrix()
    }

    renderer.current.setSize(window.innerWidth, window.innerHeight)
  }

  // Initialize threejs when the canvas is ready
  const canvas = useCallback(node => {
    if (node !== null) {
      renderer.current = new THREE.WebGLRenderer({
        logarithmicDepthBuffer: true,
        antialias: true,
        alpha: true,
        canvas: node
      })
      renderer.current.outputEncoding = THREE.GammaEncoding
      renderer.current.setClearColor(0xffffff, 0)
      init()
    }
  }, [])

  // const [dimensions, setDimensions] = useState<{width: number, height: number}>({width: 0, height: 0})

  // We need to resize the canvas according to the window
  // when the window is resized
  window.onresize = () => {
    resize()
  }

  useEffect(() => {
    console.log('> useEffect')

    // We need to resize the canvas according to the window
    resize()
  }, [])

  return (
    <>
      <div className='canvas-container'>
        <canvas
          // width={dimensions.width}
          // height={dimensions.height}
          ref={canvas}
        />
        <div
          className={`legend ${showLegend && !isOnCalendar ? 'show' : 'hide'}`}
        >
          <div>
            <b>Nombre de cas confirmés</b>
          </div>
          <div className='top'>
            <div className='clblock'>
              <div style={{ backgroundColor: '#f0ba57' }} />
            </div>
            <div className='clblock'>
              <div style={{ backgroundColor: '#e4955d' }} />
            </div>
            <div className='clblock'>
              <div style={{ backgroundColor: '#d97064' }} />
            </div>
            <div className='clblock'>
              <div style={{ backgroundColor: '#cd4b6a' }} />
            </div>
            <div className='clblock'>
              <div style={{ backgroundColor: '#c12670' }} />
            </div>
          </div>
          <div className='bottom'>
            <div className='legend-left'>
              <i className='ui icon arrow left' />1 cas
            </div>
            <div className='legend-right'>
              100 cas ou plus
              <i className='ui icon arrow right' />
            </div>
          </div>
        </div>
      </div>
      <div className='debug'>
        <div>
          <div>
            <b>Camera</b>
          </div>
          <div>
            X{' '}
            <input
              min='-300'
              max='300'
              step='1'
              type='range'
              onChange={e => {
                const c = { ...debugCamera }
                c.x = +e.target.value
                setDebugCamera(c)
              }}
            />{' '}
            {debugCamera.x}
          </div>
          <div>
            Y{' '}
            <input
              min='-300'
              max='300'
              step='1'
              type='range'
              onChange={e => {
                const c = { ...debugCamera }
                c.y = +e.target.value
                setDebugCamera(c)
              }}
            />{' '}
            {debugCamera.y}
          </div>
          <div>
            Z{' '}
            <input
              min='-300'
              max='300'
              step='1'
              type='range'
              onChange={e => {
                const c = { ...debugCamera }
                c.z = +e.target.value
                setDebugCamera(c)
              }}
            />{' '}
            {debugCamera.z}
          </div>
        </div>

        <div>
          <div>
            <b>Light</b>
          </div>
          <div>
            X{' '}
            <input
              min='-300'
              max='300'
              step='1'
              type='range'
              onChange={e => {
                const c = { ...debugLight }
                c.x = +e.target.value
                setDebugLight(c)
              }}
            />{' '}
            {debugLight.x}
          </div>
          <div>
            Y{' '}
            <input
              min='-300'
              max='300'
              step='1'
              type='range'
              onChange={e => {
                const c = { ...debugLight }
                c.y = +e.target.value
                setDebugLight(c)
              }}
            />{' '}
            {debugLight.y}
          </div>
          <div>
            Z{' '}
            <input
              min='-300'
              max='300'
              step='1'
              type='range'
              onChange={e => {
                const c = { ...debugLight }
                c.z = +e.target.value
                setDebugLight(c)
              }}
            />{' '}
            {debugLight.z}
          </div>
          <div>
            I{' '}
            <input
              min='0'
              max='10'
              step='0.01'
              type='range'
              onChange={e => {
                const c = { ...debugLight }
                c.i = +e.target.value
                setDebugLight(c)
              }}
            />{' '}
            {debugLight.i}
          </div>
        </div>

        <div>
          <div>
            <b>Color</b>
          </div>
          <div>
            R{' '}
            <input
              min='0'
              max='1'
              step='0.01'
              type='range'
              onChange={e => {
                const c = { ...debugColor }
                c.color.r = +e.target.value
                setDebugColor(c)
              }}
            />{' '}
            {debugColor.color.r}
          </div>
          <div>
            G{' '}
            <input
              min='0'
              max='1'
              step='0.01'
              type='range'
              onChange={e => {
                const c = { ...debugColor }
                c.color.g = +e.target.value
                setDebugColor(c)
              }}
            />{' '}
            {debugColor.color.g}
          </div>
          <div>
            B{' '}
            <input
              min='0'
              max='1'
              step='0.01'
              type='range'
              onChange={e => {
                const c = { ...debugColor }
                c.color.b = +e.target.value
                setDebugColor(c)
              }}
            />{' '}
            {debugColor.color.b}
          </div>
        </div>

        <div>
          <div>
            <b>Emissive</b>
          </div>
          <div>
            R{' '}
            <input
              min='0'
              max='1'
              step='0.01'
              type='range'
              onChange={e => {
                const c = { ...debugColor }
                c.emissive.r = +e.target.value
                setDebugColor(c)
              }}
            />{' '}
            {debugColor.emissive.r}
          </div>
          <div>
            G{' '}
            <input
              min='0'
              max='1'
              step='0.01'
              type='range'
              onChange={e => {
                const c = { ...debugColor }
                c.emissive.g = +e.target.value
                setDebugColor(c)
              }}
            />{' '}
            {debugColor.emissive.g}
          </div>
          <div>
            B{' '}
            <input
              min='0'
              max='1'
              step='0.01'
              type='range'
              onChange={e => {
                const c = { ...debugColor }
                c.emissive.b = +e.target.value
                setDebugColor(c)
              }}
            />{' '}
            {debugColor.emissive.b}
          </div>
        </div>
      </div>
    </>
  )
}
